// Step 3c: the start. Chips at the service's granularity (30 or 15 minutes,
// plus a band's own off-grid first/last start), or a time input for "any".
// When the band read failed the picker fails OPEN: start times from the
// opening hours, labelled "Unchecked times" — the hold decides.

import { useEffect, useId, useState, type KeyboardEvent } from "react";
import {
  addDaysYmd,
  anyStartBounds,
  pickerChips,
  startChips,
  type Band,
  type Granularity,
  type OffsetAt,
} from "../../state/availability-logic";
import { formatHm, formatHmUnambiguous, ymdOfInstant, zonedWallMs } from "../../state/tenant-time";
import { HScroller } from "./HScroller";

type Window = { startMs: number; endMs: number };
const MIN_MS = 60_000;

function ChipGroup({
  label,
  starts,
  value,
  day,
  tz,
  disabled,
  onChange,
}: {
  label: string;
  starts: number[];
  value: number | null;
  day: string;
  tz: string;
  disabled: boolean;
  onChange: (ms: number) => void;
}) {
  const selIndex = Math.max(0, starts.indexOf(value ?? Number.NaN));
  const onKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % starts.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + starts.length) % starts.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = starts.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const group = e.currentTarget.parentElement;
    (group?.children[next] as HTMLElement | undefined)?.focus();
    if (!disabled) onChange(starts[next]!);
  };
  return (
    <HScroller
      className="vt-avail-start-row"
      prevLabel="Show earlier start times"
      nextLabel="Show later start times"
      revealSelector='[aria-checked="true"]'
      revealKey={value}
    >
      <div role="radiogroup" aria-label={label} className="vt-avail-starts">
        {starts.map((ms, i) => {
          const nextDay = ymdOfInstant(ms, tz) !== day;
          // On a DST fall-back night "01:00" happens twice: the zone name tells
          // the two starts apart ("01:00 BST" / "01:00 GMT").
          const hm = formatHmUnambiguous(ms, tz);
          return (
            <button
              key={ms}
              type="button"
              role="radio"
              aria-checked={ms === value}
              aria-label={nextDay ? `${hm}, next day` : hm}
              tabIndex={i === selIndex ? 0 : -1}
              className="vt-avail-chip vt-avail-start"
              onClick={() => {
                if (!disabled) onChange(ms);
              }}
              onKeyDown={(e) => onKey(e, i)}
            >
              {hm}
              {nextDay && <small aria-hidden="true">+1</small>}
            </button>
          );
        })}
      </div>
    </HScroller>
  );
}

function AnyTime({
  bounds,
  day,
  tz,
  value,
  disabled,
  onChange,
}: {
  bounds: { minMs: number; maxMs: number };
  day: string;
  tz: string;
  value: number | null;
  disabled: boolean;
  onChange: (ms: number | null) => void;
}) {
  const hintId = useId();
  const [error, setError] = useState(false);
  // The input keeps what the guest typed; only a valid time becomes the start.
  const [text, setText] = useState(value != null ? formatHm(value, tz) : "");
  useEffect(() => {
    if (value != null) setText(formatHm(value, tz));
  }, [value, tz]);
  const min = formatHm(bounds.minMs, tz);
  const max = formatHm(bounds.maxMs, tz);
  return (
    <div className="vt-avail-any-wrap">
      <label className="vt-avail-any">
        <span>Start time</span>
        <input
          type="time"
          step={60}
          min={min}
          max={max}
          value={text}
          aria-describedby={hintId}
          aria-invalid={error || undefined}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            const m = /^(\d{2}):(\d{2})/.exec(e.target.value);
            if (!m) {
              setError(false);
              onChange(null);
              return;
            }
            const mins = Number(m[1]) * 60 + Number(m[2]);
            // A band can run past midnight: the same clock time may mean the next day.
            const ms = [zonedWallMs(day, mins, tz), zonedWallMs(addDaysYmd(day, 1), mins, tz)].find(
              (x) => bounds.minMs <= x && x <= bounds.maxMs,
            );
            setError(ms == null);
            onChange(ms ?? null);
          }}
        />
      </label>
      <p id={hintId} className="vt-avail-caption">
        Between {min} and {max}
      </p>
      {error && (
        <p role="alert" className="vt-avail-field-error">
          Pick a time between {min} and {max}.
        </p>
      )}
    </div>
  );
}

export function StartPicker({
  band,
  failOpenWindows,
  gran,
  tz,
  offsetAt,
  day,
  nowMs,
  noticeMin = 0,
  longestMin,
  value,
  onChange,
  disabled,
  fixedStarts = null,
}: {
  /** The picked band (bands answered). */
  band: Band | null;
  /** Bands could not be read: the day's opening windows ([] = the service
   *  has no hours set; the parent never fails open on a day it knows is closed). */
  failOpenWindows: Window[] | null;
  gran: Granularity;
  tz: string;
  offsetAt: OffsetAt;
  day: string;
  nowMs: number;
  /** The service's known notice (minutes): unchecked times start no earlier than now + notice. */
  noticeMin?: number;
  longestMin: number;
  value: number | null;
  onChange: (ms: number | null) => void;
  disabled: boolean;
  /** The day's fixed starts (every band a single start), offered whatever the grid. */
  fixedStarts?: number[] | null;
}) {
  if (fixedStarts && !failOpenWindows) {
    if (fixedStarts.length === 0) return <p className="vt-avail-caption">No more start times on this day.</p>;
    return (
      <div className="vt-avail-startpicker">
        <ChipGroup label="Start time" starts={fixedStarts} value={value} day={day} tz={tz} disabled={disabled} onChange={onChange} />
      </div>
    );
  }
  if (failOpenWindows) {
    // Failing open never offers what the panel already knows the hold would
    // refuse: nothing before now + the service's notice.
    const floor = Math.ceil((nowMs + Math.max(0, noticeMin) * MIN_MS) / MIN_MS) * MIN_MS;
    const spans = (failOpenWindows.length
      ? failOpenWindows.map((w) => ({ from: Math.max(w.startMs, floor), to: w.endMs - longestMin * MIN_MS }))
      : [{ from: Math.max(zonedWallMs(day, 0, tz), floor), to: zonedWallMs(day, 23 * 60 + 59, tz) }]
    ).filter((s) => s.from <= s.to);
    if (spans.length === 0) {
      return <p className="vt-avail-caption">No more unchecked times on this day.</p>;
    }
    if (gran === "any" || failOpenWindows.length === 0) {
      return (
        <div className="vt-avail-startpicker">
          <p className="vt-avail-caption">Unchecked times</p>
          <AnyTime
            bounds={{ minMs: Math.ceil(spans[0]!.from / MIN_MS) * MIN_MS, maxMs: Math.floor(spans[spans.length - 1]!.to / MIN_MS) * MIN_MS }}
            day={day}
            tz={tz}
            value={value}
            disabled={disabled}
            onChange={onChange}
          />
        </div>
      );
    }
    const starts = [
      ...new Set(
        spans.flatMap((s) => startChips({ opensAtMs: s.from, lastStartMs: s.to, noticeTruncated: false }, gran, offsetAt)),
      ),
    ].sort((a, b) => a - b);
    return (
      <div className="vt-avail-startpicker">
        <p className="vt-avail-caption">Unchecked times</p>
        <ChipGroup label="Unchecked start time" starts={starts} value={value} day={day} tz={tz} disabled={disabled} onChange={onChange} />
      </div>
    );
  }
  if (!band) return null;
  // Bands answer for the moment they were read; a start the clock has since
  // passed is not offered (the Add re-check would refuse it anyway).
  const floor = Math.ceil(nowMs / MIN_MS) * MIN_MS;
  if (gran === "any") {
    const b = anyStartBounds(band);
    const bounds = b && Math.max(b.minMs, floor) <= b.maxMs ? { minMs: Math.max(b.minMs, floor), maxMs: b.maxMs } : null;
    if (!bounds) return <p className="vt-avail-caption">No more start times in this window.</p>;
    return (
      <div className="vt-avail-startpicker">
        <AnyTime bounds={bounds} day={day} tz={tz} value={value} disabled={disabled} onChange={onChange} />
      </div>
    );
  }
  // The picked start stays on offer while its band still contains it, even
  // when a refresh moved the band's edges.
  const chips = pickerChips(band, gran, offsetAt, nowMs).filter((ms) => ms >= floor);
  const starts =
    value != null && value >= floor && band.opensAtMs <= value && value <= band.lastStartMs && !chips.includes(value)
      ? [...chips, value].sort((a, b) => a - b)
      : chips;
  if (starts.length === 0) return <p className="vt-avail-caption">No more start times in this window.</p>;
  return (
    <div className="vt-avail-startpicker">
      <ChipGroup label="Start time" starts={starts} value={value} day={day} tz={tz} disabled={disabled} onChange={onChange} />
    </div>
  );
}
